import type { User } from 'firebase/auth'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { useLazyQuery } from '@apollo/client'

import client from '../apolloClient'
import Loader from '../components/Loader'
import { GET_CONSUMER_CWA } from '@consumer/graphql'
import { auth } from '../utils/firebase'
import { AuthContext, CWAUser } from './AuthContext'

const isNotLocalDev = import.meta.env.REACT_APP_STAGE !== 'local'

/** Provides Firebase authentication state and the resolved consumer user object to the tree. */
function AuthContextProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null)
  const [firebaseInitialized, setFirebaseInitialized] = useState<boolean>(false)
  // True until Firebase definitively responds. The 3-second fallback shows children
  // (sets firebaseInitialized) but deliberately does NOT clear this flag so that
  // auth-dependent routes can distinguish "loading" from "confirmed sign-out".
  const [firebaseAuthLoading, setFirebaseAuthLoading] = useState<boolean>(true)
  const [user, setUser] = useState<CWAUser | null>(null)
  const [getConsumer] = useLazyQuery<{ getConsumer: CWAUser }>(GET_CONSUMER_CWA)

  const handleSetFirebaseUser = useCallback(async (fbUser: User | null) => {
    setFirebaseUser(fbUser)
    setFirebaseInitialized(true)
  }, [])

  useEffect(() => {
    let mounted = true
    // don't run this for localDev since we instantiate an empty Firebase Auth object
    if (auth && isNotLocalDev) {
      // Track whether the fallback fired before Firebase responded. If it did and
      // Firebase later confirms a signed-in user, we refetch so authenticated order
      // fields replace the anonymous data already loaded.
      let fallbackFired = false

      // If Firebase is slow or unreachable, fall back to anonymous after 3 seconds
      // so the user can still reach the tracking UI without waiting indefinitely.
      // Note: firebaseAuthLoading is intentionally NOT cleared here — auth-dependent
      // routes should gate redirects on !firebaseAuthLoading to avoid evicting a
      // signed-in user whose Firebase initialization just happened to be slow.
      const fallbackTimer = setTimeout(() => {
        if (mounted) {
          fallbackFired = true
          handleSetFirebaseUser(null)
        }
      }, 3000)

      // listen for auth state changes from Firebase auth and update firebaseUser
      const unsubscribe = auth?.onAuthStateChanged(user => {
        clearTimeout(fallbackTimer)
        // If the fallback already fired with null and Firebase now confirms a signed-in
        // user, refetch so the order query runs with authenticated credentials instead
        // of reusing the anonymous response already in the cache.
        if (fallbackFired && user) {
          client.refetchQueries({ include: 'all' })
        }
        setFirebaseAuthLoading(false)
        handleSetFirebaseUser(user)
      })
      return () => {
        mounted = false
        clearTimeout(fallbackTimer)
        unsubscribe?.()
      }
    } else if (!isNotLocalDev) {
      // For local dev, initialize to prevent infinite loading
      if (mounted) {
        setFirebaseAuthLoading(false)
        setFirebaseInitialized(true)
        setFirebaseUser(null)
      }
    }
    return () => {
      mounted = false
    }
  }, [handleSetFirebaseUser])

  const logout = useCallback(async () => {
    if (auth.currentUser) {
      await auth.signOut()
      setFirebaseUser(null)
      setUser(null)
      // force refetch to clear fields on order
      client.refetchQueries({ include: 'all' })
    }
  }, [])

  const reloadUser = useCallback(async () => {
    if (firebaseUser) {
      const consumer = await getConsumer({ fetchPolicy: 'network-only' }).catch(() => null)
      setUser(consumer?.data?.getConsumer ?? null)
    } else {
      setUser(null)
    }
  }, [firebaseUser, getConsumer])

  // Reload user when firebaseUser changes
  useEffect(() => {
    let mounted = true
    ;(async () => {
      if (firebaseUser) {
        const consumer = await getConsumer({ fetchPolicy: 'network-only' }).catch(() => null)
        if (mounted) setUser(consumer?.data?.getConsumer ?? null)
      } else {
        if (mounted) setUser(null)
      }
    })()
    return () => {
      mounted = false
    }
  }, [firebaseUser, getConsumer])

  const state = useMemo(() => {
    return {
      firebaseUser,
      user,
      logout,
      reloadUser,
      firebaseAuthLoading,
    }
  }, [firebaseUser, user, logout, reloadUser, firebaseAuthLoading])

  return <AuthContext.Provider value={state}>{firebaseInitialized ? children : <Loader />}</AuthContext.Provider>
}

export default AuthContextProvider
